import { clsx } from 'clsx'
import { useRouter } from 'expo-router'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Slider, type TSliderProps } from '@/src/elements/slider/Slider'
import { Typography } from '@/src/elements/text/Typography'
import { ICON } from '@/src/general/enums/icons'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { cn } from '@/src/general/utils/cn'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { JourneySliderEmptyState } from './JourneySliderEmptyState'
import { JourneyThumbnail, type TJourneyThumbnailProps } from './JourneyThumbnail'
import { JourneyThumbnailPlaceholder } from './JourneyThumbnail/JourneyThumbnailPlaceholder'

export const JOURNEY_THUMBNAIL_WIDTH_CLASSNAME = 'max-w-[360px] w-[75vw]'
export const getSliderClasses = (
  items?: any[],
  classes?: TSliderProps<any>['classes'],
): TSliderProps<any>['classes'] => {
  if (items?.length === 0) return { ...classes, base: cn(classes?.base, 'order-1') }
  if (items?.length === 1)
    return { ...classes, slot: cn(classes?.slot, 'w-full'), container: cn(classes?.container, 'w-full') }
  return classes
}

const getItemKey = (item: TJourneyThumbnailProps) => item.community.id.toString()

const renderPlaceholderItem = (key: number) => (
  <JourneyThumbnailPlaceholder key={key} className={JOURNEY_THUMBNAIL_WIDTH_CLASSNAME} />
)

export type TJourneySliderBaseProps = {
  journeyItems?: TJourneyThumbnailProps[]
  showHistoryButton?: boolean
}

export const JourneySliderBase = ({ journeyItems, showHistoryButton }: TJourneySliderBaseProps) => {
  const { t } = useTranslate()
  const router = useRouter()
  const { routes } = useRoutes()

  const hasOneItem = journeyItems?.length === 1
  const hasThreeItems = journeyItems?.length === 3
  const autoScrollIndex = journeyItems?.findIndex((item) => !item.subscription?.isPrayedToday)
  const firstUnprayedJourneyIndex = autoScrollIndex !== undefined && autoScrollIndex >= 0 ? autoScrollIndex : undefined

  const { lessThan } = useDimensions()

  const renderItem = (item: TJourneyThumbnailProps) => (
    <JourneyThumbnail
      className={clsx(
        !lessThan.md || hasOneItem
          ? `w-full flex-1 min-h-[220px] ${hasOneItem ? '' : 'max-w-[66%] md:max-w-full'}`
          : JOURNEY_THUMBNAIL_WIDTH_CLASSNAME,
      )}
      {...item}
    />
  )

  const historyButton = showHistoryButton && (
    <Button
      onPress={() => router.navigate(routes.subscriptionsHistory())}
      colors="default"
      variant="light"
      size="xs"
      endIcon={ICON.CHEVRON_RIGHT}
      className="self-baseline"
    >
      <FormattedMessage id="journey.see-history" />
    </Button>
  )

  if (lessThan.md) {
    return (
      <Slider<TJourneyThumbnailProps>
        title={t('journey.novenas-and-journeys')}
        linkButton={historyButton}
        list={journeyItems}
        autoScrollIndex={firstUnprayedJourneyIndex}
        classes={getSliderClasses(journeyItems, { container: 'h-72 px-4', titleContainer: 'px-4' })}
        emptyState={<JourneySliderEmptyState />}
        renderItem={renderItem}
        renderPlaceholderItem={renderPlaceholderItem}
        getItemKey={getItemKey}
      />
    )
  }

  return (
    <View className="p-4 flex flex-col gap-4 w-full pt-8">
      <View className="flex-row justify-between">
        <Typography heading="h2" bold size="xl" contrast="high" colors="default">
          <FormattedMessage id="journey.novenas-and-journeys" />
        </Typography>
        {historyButton}
      </View>
      <View className={clsx('flex flex-row flex-wrap', (hasOneItem || hasThreeItems || !journeyItems) && 'gap-2')}>
        {!journeyItems && Array.from({ length: 2 }, (_, index) => renderPlaceholderItem(index))}
        {journeyItems && journeyItems.length === 0 && <JourneySliderEmptyState />}
        {hasOneItem && renderItem(journeyItems[0])}
        {hasThreeItems && (
          <>
            <View className="w-[60%]">{renderItem(journeyItems[0])}</View>
            <View className="flex-1 flex flex-col gap-2">
              {renderItem(journeyItems[1])}
              {renderItem(journeyItems[2])}
            </View>
          </>
        )}
        {!hasOneItem &&
          !hasThreeItems &&
          journeyItems?.map((item, index) => (
            <View className={clsx('max-w-[50%] w-full pb-2', index % 2 === 0 ? 'pr-1' : 'pl-1')} key={getItemKey(item)}>
              {renderItem(item)}
            </View>
          ))}
      </View>
    </View>
  )
}
